import { useState, useRef, useEffect } from "react";
import { useNitroStore } from "../store/Store";
import { useNitroCAStore } from "../store/CompetitiveAnalysisStore";
import NitroService from "../utils/nitro.service";
import { SelectedOrigin } from "../pages/home/favoriteOrigin/AddOriginColumn";
import { IFavoriteOrigin, PriceField } from "../types/IFavoriteOrigins";

interface MutateHookProps {
  user: string;
  product: string;
}

export const useMutateFavoriteOrigins = ({
  user,
  product,
}: MutateHookProps) => {
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const setFetchFavoriteOrigins = useNitroStore(
    (state) => state.setFetchFavoriteOrigins
  );
  const triggerRefetchMultiLocation = useNitroCAStore(
    (state) => state.triggerRefetchMultiLocation
  );
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const addOrigin = async (
    selectedOrigin: SelectedOrigin,
    type: "koch" | "competitor"
  ) => {
    if (!user || !product) {
      console.error("Cannot add origin: User or Product is not defined.");
      return;
    }
    setIsMutating(true);
    try {
      // when origin is added Truck is enabled by default, rail and barge disabled
      await NitroService.createFavoriteOrigin(
        user,
        selectedOrigin.id,
        selectedOrigin.name,
        product,
        type === "competitor",
        true,
        false,
        false
      );
      setFetchFavoriteOrigins(true);
    } catch (e: any) {
      setError(e);
    } finally {
      setIsMutating(false);
    }
  };

  const removeOrigin = async (
    originId: string,
    type: "koch" | "competitor"
  ) => {
    if (!user || !product) {
      console.error("Cannot remove origin: User or Product is not defined.");
      return;
    }
    setIsMutating(true);
    try {
      await NitroService.deleteFavoriteOrigin(
        user,
        product,
        originId,
        type === "competitor"
      );
      setFetchFavoriteOrigins(true);
    } catch (e: any) {
      setError(e);
    } finally {
      setIsMutating(false);
    }
  };

  const updateOriginPrice = (
    origin: IFavoriteOrigin,
    field: PriceField,
    value: number | null // allow input row selected for no price value
  ) => {
    if (!user || !product) {
      console.error("Cannot update price: User or Product is not defined.");
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      setIsMutating(true);
      setError(null);

      const updatedOrigin = {
        ...origin,
        [`${field}Price`]: value,
      };

      try {
        await NitroService.updateFavoriteOrigin(updatedOrigin, user, product);
        setFetchFavoriteOrigins(true);
        triggerRefetchMultiLocation();
      } catch (e: any) {
        setError(e);
        console.error("Failed to update origin price:", e);
      } finally {
        setIsMutating(false);
      }
    }, 500);
  };

  return { addOrigin, removeOrigin, updateOriginPrice, isMutating, error };
};
